import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { userAPI, transactionAPI } from '../../services/api';
import {
  Wallet, ArrowLeftRight, Clock, ArrowRight, Code2,
  BarChart2, Banknote, ShoppingBag, ChevronRight,
  Activity, ShieldCheck, Zap, CheckCircle2, XCircle,
  AlertCircle, TrendingUp,
} from 'lucide-react';

/* ── helpers ─────────────────────────────────────────────────── */
const fmtINR = (n) =>
  '₹ ' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// res.data = { success, data: { wallet_balance, ... } }
const d = (raw, key) => raw?.data?.[key] ?? raw?.[key];

const fmtDate = (iso) => {
  if (!iso) return '—';
  const dt = new Date(iso);
  return dt.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true });
};

const shortId = (id) => id ? id.slice(-10).toUpperCase() : '—';

/* ── status config ───────────────────────────────────────────── */
const STATUS = {
  success:   { label: 'Success',   color: '#065F46', bg: '#D1FAE5', icon: CheckCircle2 },
  failed:    { label: 'Failed',    color: '#991B1B', bg: '#FEE2E2', icon: XCircle      },
  initiated: { label: 'Initiated', color: '#92400E', bg: '#FEF3C7', icon: AlertCircle  },
  pending:   { label: 'Pending',   color: '#92400E', bg: '#FEF3C7', icon: AlertCircle  },
};
const getStatus = (s) => STATUS[s?.toLowerCase()] || STATUS.initiated;

/* ── design tokens ───────────────────────────────────────────── */
const C = {
  bg:         '#f4f4f9',
  surface:    '#FFFFFF',
  border:     '#e8e8f1',
  borderSoft: '#f0f0f6',
  ink:        '#14142b',
  body:       '#4a4a6a',
  muted:      '#6e6e86',
  primary:    '#7c3aed',
  primarySoft:'#f1ebff',
  primaryTint:'#ddd6fe',
  success:    '#10B981',
  successSoft:'#D1FAE5',
  warning:    '#F59E0B',
  warnSoft:   '#FEF3C7',
  danger:     '#EF4444',
  dangerSoft: '#FEE2E2',
  radius:     14,
  radiusSm:   8,
};

/* ── sub-components ──────────────────────────────────────────── */
const KpiCard = ({ icon: Icon, iconColor, iconBg, label, value, loading, linkTo, linkLabel }) => (
  <div style={{
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: C.radius,
    padding: '20px 22px',
    display: 'flex', flexDirection: 'column', gap: 14,
    boxShadow: '0 1px 3px rgba(20,20,43,.04)',
    transition: 'box-shadow .15s ease',
  }}
  onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(124,58,237,.08)'}
  onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 3px rgba(20,20,43,.04)'}
  >
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.07em' }}>
        {label}
      </span>
      <div style={{
        width: 36, height: 36, borderRadius: 10,
        background: iconBg, color: iconColor,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <Icon size={16} strokeWidth={1.8} />
      </div>
    </div>
    <div style={{ fontSize: 26, fontWeight: 800, color: C.ink, letterSpacing: '-0.02em', lineHeight: 1, minHeight: 30, display: 'flex', alignItems: 'center' }}>
      {loading ? <span style={{ color: C.borderSoft }}>——</span> : value}
    </div>
    <Link to={linkTo} style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: 12, fontWeight: 600, color: C.primary, textDecoration: 'none',
    }}>
      {linkLabel} <ArrowRight size={11} />
    </Link>
  </div>
);

const QuickAction = ({ to, icon: Icon, iconColor, iconBg, title, desc }) => (
  <Link to={to} style={{
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '11px 14px',
    borderRadius: C.radiusSm,
    border: `1px solid ${C.border}`,
    background: C.surface,
    textDecoration: 'none',
    transition: 'all .13s ease',
  }}
  onMouseEnter={e => { e.currentTarget.style.background = C.primarySoft; e.currentTarget.style.borderColor = C.primaryTint; }}
  onMouseLeave={e => { e.currentTarget.style.background = C.surface; e.currentTarget.style.borderColor = C.border; }}
  >
    <div style={{
      width: 34, height: 34, borderRadius: 9,
      background: iconBg, color: iconColor,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>
      <Icon size={15} strokeWidth={1.8} />
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: C.ink }}>{title}</div>
      <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>{desc}</div>
    </div>
    <ChevronRight size={14} color={C.muted} strokeWidth={1.8} />
  </Link>
);

const InfoRow = ({ label, value }) => (
  <div style={{
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '9px 0', borderBottom: `1px solid ${C.borderSoft}`, fontSize: 13,
  }}>
    <span style={{ color: C.muted, fontWeight: 500 }}>{label}</span>
    <span style={{ color: C.ink, fontWeight: 600 }}>{value}</span>
  </div>
);

const SectionTitle = ({ children, action }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
    <span style={{
      fontSize: 12, fontWeight: 700, color: C.muted,
      textTransform: 'uppercase', letterSpacing: '.07em',
    }}>{children}</span>
    {action}
  </div>
);

/* ── Recent transaction row ──────────────────────────────────── */
const TxnRow = ({ txn, isLast }) => {
  const st = getStatus(txn.status);
  const StatusIcon = st.icon;
  const source = txn.gateway_response?.shop_domain || txn.description || '—';

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr auto',
      gap: 12,
      padding: '12px 0',
      borderBottom: isLast ? 'none' : `1px solid ${C.borderSoft}`,
      alignItems: 'center',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
        {/* Status icon */}
        <div style={{
          width: 34, height: 34, borderRadius: 9,
          background: st.bg, color: st.color,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <StatusIcon size={15} strokeWidth={2} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.ink, display: 'flex', alignItems: 'center', gap: 6 }}>
            <code style={{ fontFamily: 'Menlo, monospace', fontSize: 11, color: C.primary }}>
              {shortId(txn.transaction_id)}
            </code>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 3,
              fontSize: 10, fontWeight: 700, color: st.color,
              background: st.bg, borderRadius: 4, padding: '1px 7px',
              textTransform: 'uppercase', letterSpacing: '.04em',
            }}>
              {st.label}
            </span>
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {source} · {fmtDate(txn.created_at)}
          </div>
        </div>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>{fmtINR(txn.amount)}</div>
      </div>
    </div>
  );
};

/* ── SetupStep ───────────────────────────────────────────────── */
const SetupStep = ({ done, icon: Icon, title, desc, link }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 11,
    padding: '10px 0', borderBottom: `1px solid ${C.borderSoft}`,
  }}>
    <div style={{
      width: 30, height: 30, borderRadius: 8,
      background: done ? C.successSoft : '#F1F5F9',
      color: done ? '#065F46' : C.muted,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>
      <Icon size={14} strokeWidth={1.8} />
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: done ? C.ink : C.body }}>{title}</div>
      <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>{desc}</div>
    </div>
    {done
      ? <CheckCircle2 size={15} color={C.success} strokeWidth={2} />
      : link && <Link to={link} style={{ fontSize: 11, fontWeight: 700, color: C.primary, textDecoration: 'none', whiteSpace: 'nowrap' }}>Set up →</Link>
    }
  </div>
);

/* ── main component ──────────────────────────────────────────── */
const UserDashboard = () => {
  const { user } = useAuth();
  const [raw,        setRaw]        = useState(null);
  const [summary,    setSummary]    = useState(null);
  const [loading,    setLoading]    = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [dashRes, summaryRes] = await Promise.allSettled([
          userAPI.getDashboard(),
          transactionAPI.getTransactionSummary(),
        ]);
        if (dashRes.status === 'fulfilled')    setRaw(dashRes.value.data || {});
        if (summaryRes.status === 'fulfilled') setSummary(summaryRes.value.data?.data || null);
      } catch {}
      finally { setLoading(false); }
    })();
  }, []);

  // Parse from API response shape: { success, data: { wallet_balance, total_transactions, pending_requests, recent_transactions } }
  const walletBalance     = d(raw, 'wallet_balance') ?? 0;
  const totalTxns         = d(raw, 'total_transactions') ?? 0;
  const pendingRequests   = d(raw, 'pending_requests') ?? 0;
  const recentTxns        = d(raw, 'recent_transactions') ?? [];

  const netEarnings = summary?.amounts?.net_earnings ?? 0;

  const accountStatus      = (user?.account_status || 'inactive').toLowerCase();
  const isActive           = accountStatus === 'active';
  const settlementCycle    = user?.settlement_cycle ?? 't3';
  const settlementPct      = user?.settlement_percentage ?? '3.00';
  const cycleLabel         = settlementCycle === 't1' ? 'T+1' : settlementCycle === 't2' ? 'T+2' : 'T+3';

  const statusColor = isActive ? C.success : C.danger;
  const statusBg    = isActive ? C.successSoft : C.dangerSoft;

  const kpis = [
    {
      label: 'Wallet Balance',
      value: fmtINR(walletBalance),
      icon: Wallet,
      iconColor: C.primary,
      iconBg: C.primarySoft,
      linkTo: '/user/wallet',
      linkLabel: 'Top up wallet',
    },
    {
      label: 'Total Transactions',
      value: Number(totalTxns).toLocaleString('en-IN'),
      icon: ArrowLeftRight,
      iconColor: '#7C3AED',
      iconBg: '#F5F3FF',
      linkTo: '/user/transactions',
      linkLabel: 'View all',
    },
    {
      label: 'Pending Requests',
      value: Number(pendingRequests).toLocaleString('en-IN'),
      icon: Clock,
      iconColor: C.warning,
      iconBg: C.warnSoft,
      linkTo: '/user/withdrawal',
      linkLabel: 'Manage',
    },
    {
      label: 'Net Earnings',
      value: fmtINR(netEarnings),
      icon: TrendingUp,
      iconColor: C.success,
      iconBg: C.successSoft,
      linkTo: '/user/transactions',
      linkLabel: 'View all',
    },
  ];

  const quickActions = [
    { to: '/user/transactions', icon: Activity,    iconColor: '#7C3AED', iconBg: '#F5F3FF',    title: 'Transactions',   desc: 'All incoming payments'        },
    { to: '/user/settlements',  icon: BarChart2,   iconColor: C.primary, iconBg: C.primarySoft, title: 'Settlements',  desc: 'T+1 batched payouts'          },
    { to: '/user/withdrawal',   icon: Banknote,    iconColor: C.success, iconBg: C.successSoft, title: 'Withdraw Funds', desc: 'Manual withdrawal request'  },
    { to: '/user/integration',  icon: Code2,       iconColor: '#7c3aed', iconBg: '#E0F2FE',     title: 'API Integration', desc: 'Keys, samples & webhooks' },
    { to: '/user/shopify',      icon: ShoppingBag, iconColor: '#78350F', iconBg: '#FEF3C7',     title: 'Shopify Setup',  desc: 'Connect your Shopify store' },
  ];

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>

      {/* ── Welcome bar ──────────────────────────────────────── */}
      <div style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: C.radius,
        padding: '20px 26px',
        marginBottom: 22,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 16, flexWrap: 'wrap',
        boxShadow: '0 1px 3px rgba(20,20,43,.04)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{
            width: 46, height: 46, borderRadius: 13,
            background: 'linear-gradient(135deg, #7c3aed 0%, #7c3aed 100%)',
            color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 800, fontSize: 18, flexShrink: 0,
          }}>
            {(user?.username || 'M').trim().slice(0, 1).toUpperCase()}
          </div>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, color: C.ink, letterSpacing: '-0.01em' }}>
              Welcome back, {user?.username || 'Merchant'}
            </div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
              {new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '5px 13px', borderRadius: 999,
            background: statusBg,
            border: `1px solid ${isActive ? '#6EE7B7' : '#FCA5A5'}`,
            fontSize: 12, fontWeight: 700, color: statusColor,
          }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: statusColor }} />
            {accountStatus.charAt(0).toUpperCase() + accountStatus.slice(1)}
          </div>
          <Link to="/user/integration" style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '8px 16px', borderRadius: C.radiusSm,
            background: 'linear-gradient(135deg, #7c3aed 0%, #7c3aed 100%)',
            color: '#fff', fontSize: 13, fontWeight: 600, textDecoration: 'none',
            boxShadow: '0 2px 8px rgba(124,58,237,.25)',
          }}>
            <Zap size={13} strokeWidth={2} />
            API Setup
          </Link>
        </div>
      </div>

      {/* ── KPI cards ────────────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 16, marginBottom: 22,
      }}>
        {kpis.map((k, i) => <KpiCard key={i} {...k} loading={loading} />)}
      </div>

      {/* ── Lower grid: transactions + side panel ────────────── */}
      <div className="dashboard-lower-grid">

        {/* LEFT — Recent transactions */}
        <div style={{
          background: C.surface, border: `1px solid ${C.border}`,
          borderRadius: C.radius, padding: '20px 22px',
          boxShadow: '0 1px 3px rgba(20,20,43,.04)',
        }}>
          <SectionTitle action={
            <Link to="/user/transactions" style={{ fontSize: 12, fontWeight: 600, color: C.primary, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 3 }}>
              View all <ArrowRight size={11} />
            </Link>
          }>
            Recent Transactions
          </SectionTitle>

          {loading ? (
            <div style={{ padding: '32px 0', textAlign: 'center', color: C.muted, fontSize: 13 }}>Loading…</div>
          ) : recentTxns.length === 0 ? (
            <div style={{ padding: '40px 0', textAlign: 'center' }}>
              <ArrowLeftRight size={28} color={C.border} strokeWidth={1.4} style={{ margin: '0 auto 10px' }} />
              <div style={{ fontSize: 14, fontWeight: 600, color: C.muted }}>No transactions yet</div>
              <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>Transactions will appear here once payments start</div>
            </div>
          ) : (
            recentTxns.map((txn, i) => (
              <TxnRow key={txn.id} txn={txn} isLast={i === recentTxns.length - 1} />
            ))
          )}
        </div>

        {/* RIGHT — Side cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Account info */}
          <div style={{
            background: C.surface, border: `1px solid ${C.border}`,
            borderRadius: C.radius, padding: '18px 20px',
            boxShadow: '0 1px 3px rgba(20,20,43,.04)',
          }}>
            <SectionTitle>Account</SectionTitle>
            <InfoRow label="Status" value={
              <span style={{
                background: statusBg, color: statusColor,
                borderRadius: 999, padding: '2px 10px', fontSize: 11, fontWeight: 700,
              }}>
                {accountStatus.charAt(0).toUpperCase() + accountStatus.slice(1)}
              </span>
            } />
            <InfoRow label="Merchant ID" value={
              <code style={{
                fontFamily: 'Menlo, Consolas, monospace', fontSize: 10,
                background: C.primarySoft, color: C.primary, padding: '2px 7px', borderRadius: 4,
              }}>
                {user?.mid || '—'}
              </code>
            } />
            <InfoRow label="Settlement Cycle" value={
              <span style={{
                background: C.primarySoft, color: C.primary,
                borderRadius: 999, padding: '2px 10px', fontSize: 11, fontWeight: 700,
              }}>{cycleLabel}</span>
            } />
            <InfoRow label="Settlement %" value={`${settlementPct}%`} />
            <div style={{ padding: '9px 0', fontSize: 13 }}>
              <span style={{ color: C.muted, fontWeight: 500 }}>Email</span>
              <div style={{ color: C.ink, fontWeight: 600, marginTop: 3, wordBreak: 'break-all', fontSize: 12 }}>
                {user?.email || '—'}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <Link to="/user/profile" style={{
                flex: 1, textAlign: 'center', padding: '7px 10px',
                borderRadius: C.radiusSm, fontSize: 12, fontWeight: 600,
                background: C.primarySoft, color: C.primary,
                border: `1px solid ${C.primaryTint}`, textDecoration: 'none',
              }}>Profile</Link>
              <Link to="/user/wallet" style={{
                flex: 1, textAlign: 'center', padding: '7px 10px',
                borderRadius: C.radiusSm, fontSize: 12, fontWeight: 600,
                background: C.successSoft, color: C.success,
                border: '1px solid #6EE7B7', textDecoration: 'none',
              }}>Wallet</Link>
            </div>
          </div>

          {/* Quick actions */}
          <div style={{
            background: C.surface, border: `1px solid ${C.border}`,
            borderRadius: C.radius, padding: '18px 20px',
            boxShadow: '0 1px 3px rgba(20,20,43,.04)',
          }}>
            <SectionTitle>Quick Actions</SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              {quickActions.map((a, i) => <QuickAction key={i} {...a} />)}
            </div>
          </div>

          {/* Get started */}
          <div style={{
            background: C.surface, border: `1px solid ${C.border}`,
            borderRadius: C.radius, padding: '18px 20px',
            boxShadow: '0 1px 3px rgba(20,20,43,.04)',
          }}>
            <SectionTitle>Get Started</SectionTitle>
            <SetupStep done={isActive}               icon={ShieldCheck}  title="Activate account"  desc="Approved by admin"             link={null}              />
            <SetupStep done={!!user?.default_webhook_url} icon={Code2}   title="Set webhook URL"   desc="Receive payment callbacks"     link="/user/integration" />
            <SetupStep done={!!user?.shopify_domain}  icon={ShoppingBag}  title="Connect Shopify"   desc="Enable Shopify checkout"       link="/user/shopify"     />
          </div>

        </div>
      </div>

    <style>{`
      @keyframes spin{to{transform:rotate(360deg)}}
      .dashboard-lower-grid {
        display: grid;
        grid-template-columns: 1fr 320px;
        gap: 18px;
        align-items: flex-start;
      }
      @media (max-width: 860px) {
        .dashboard-lower-grid { grid-template-columns: 1fr; }
      }
    `}</style>
    </div>
  );
};

export default UserDashboard;
